import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";
import { inputCls, areaCls, btnPrimary, chip, label } from "@/lib/ui";

const WindowPicker = ({ windows, value, onChange }) => (
  <div className="space-y-2">
    {windows?.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {windows.map((w) => (
          <button type="button" key={w} data-testid={`window-chip-${w}`} className={chip(value === w)} onClick={() => onChange(w)}>{w}</button>
        ))}
      </div>
    )}
    <input data-testid="window-custom-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Or suggest a time, e.g. Sat 11am" className={inputCls} />
  </div>
);

export const OfferDialog = ({ open, onOpenChange, listing, onDone }) => {
  const [amount, setAmount] = useState("");
  const [win, setWin] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/offers`, { amount: Number(amount), proposed_window: win || null });
      if (data.status === "auto_declined") toast.info("The seller can't accept that amount. You can try a different offer.");
      else toast.success("Offer sent");
      onOpenChange(false);
      onDone ? onDone() : nav(`/inbox/${data.conversation_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offer-dialog" className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle>Make an offer</DialogTitle>
          <DialogDescription>Listed at {money(listing.price)}. The seller can accept, decline or counter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <span className={label}>Your offer</span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input data-testid="offer-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls + " pl-7 text-lg font-bold"} />
            </div>
          </div>
          <div>
            <span className={label}>Pickup window (optional)</span>
            <WindowPicker windows={listing.pickup_windows} value={win} onChange={setWin} />
          </div>
          <button data-testid="offer-submit-btn" disabled={busy || !amount} onClick={submit} className={btnPrimary + " w-full"}>Send offer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PickupDialog = ({ open, onOpenChange, listing, offer, onDone }) => {
  const [win, setWin] = useState(offer?.proposed_window || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const price = offer?.status === "accepted" ? offer.final_amount : listing.price;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/appointments`, { proposed_window: win, offer_id: offer?.status === "accepted" ? offer.id : null, note: note || null });
      toast.success("Pickup request sent to the seller");
      onOpenChange(false);
      onDone ? onDone() : nav(`/inbox/${data.conversation_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pickup-dialog" className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule pickup at {money(price)}</DialogTitle>
          <DialogDescription>Propose a window. The seller confirms the exact time and meeting place, then you confirm.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <WindowPicker windows={listing.pickup_windows} value={win} onChange={setWin} />
          <textarea data-testid="pickup-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. I'll bring a truck)" className={areaCls} />
          <button data-testid="pickup-submit-btn" disabled={busy || win.trim().length < 2} onClick={submit} className={btnPrimary + " w-full"}>Request pickup</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const QuestionDialog = ({ open, onOpenChange, listing }) => {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/questions`, { body });
      toast.success("Question sent");
      onOpenChange(false);
      nav(`/inbox/${data.conversation_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="question-dialog" className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle>Ask a question</DialogTitle>
          <DialogDescription>Ask something specific — dimensions, condition, what's included. No commitment to buy.</DialogDescription>
        </DialogHeader>
        <textarea data-testid="question-input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Does the drawer on the bottom left close fully?" className={areaCls} />
        <button data-testid="question-submit-btn" disabled={busy || body.trim().length < 2} onClick={submit} className={btnPrimary + " w-full"}>Send question</button>
      </DialogContent>
    </Dialog>
  );
};

export const ReportDialog = ({ open, onOpenChange, targetType, targetId }) => {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const reasons = targetType === "listing" ? ["Prohibited item", "Scam or misleading", "Wrong category", "Duplicate / spam"] : ["Harassment", "Scam attempt", "Spam", "Unsafe behavior"];
  const submit = async () => {
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, details });
      toast.success("Report sent to our trust team");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle>Report {targetType}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {reasons.map((r) => <button key={r} data-testid={`report-reason-${r}`} className={chip(reason === r)} onClick={() => setReason(r)}>{r}</button>)}
        </div>
        <textarea data-testid="report-details-input" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Details (optional)" className={areaCls} />
        <button data-testid="report-submit-btn" disabled={!reason} onClick={submit} className={btnPrimary + " w-full"}>Submit report</button>
      </DialogContent>
    </Dialog>
  );
};
